import { ArrowLeft } from 'lucide-react';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import Alert from '@/components/Alert';
import AuthLayout from '@/components/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { ChainlitContext } from 'client-types/*';

interface FormValues {
  email: string;
  organization: string;
  intended_use: string;
  roe_acknowledged: boolean;
}

export default function Signup() {
  const apiClient = useContext(ChainlitContext) as any;
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
      organization: '',
      intended_use: '',
      roe_acknowledged: false
    }
  });

  const onSubmit = async (data: FormValues) => {
    setError(null);
    setSubmitting(true);
    try {
      const result = await apiClient.submitSignup({
        email: data.email.trim().toLowerCase(),
        organization: data.organization.trim(),
        intended_use: data.intended_use.trim(),
        roe_acknowledged: data.roe_acknowledged,
        provider: 'credentials'
      });
      navigate('/signup/success', {
        state: { verificationEmail: Boolean(result?.verification_email) }
      });
    } catch (err: any) {
      if (err?.status === 409 || /already.pending/i.test(err?.message ?? '')) {
        setError('A request for this email is already pending review.');
      } else {
        setError(
          err?.message ||
            'Could not submit your request. Please try again later.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout subtitle="Register" wide>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <button
          type="button"
          onClick={() =>
            location.key !== 'default' ? navigate(-1) : navigate('/welcome')
          }
          className="flex items-center gap-1 self-start text-sm text-primary underline-offset-4 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="text-center">
          <h1 className="text-2xl font-bold">Register</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            We'll email you with next steps once you register.
          </p>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <div className="grid gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoFocus
            disabled={submitting}
            placeholder="you@organization.com"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
                message: 'Enter a valid email address'
              }
            })}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="organization">Organization</Label>
          <Input
            id="organization"
            disabled={submitting}
            placeholder="Company, agency, or team"
            {...register('organization', {
              required: 'Organization is required',
              maxLength: {
                value: 200,
                message: 'Organization must be 200 characters or fewer'
              }
            })}
          />
          {errors.organization && (
            <p className="text-sm text-destructive">
              {errors.organization.message}
            </p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="intended_use">Intended use</Label>
          <textarea
            id="intended_use"
            rows={4}
            disabled={submitting}
            placeholder="What do you plan to use Crimson Raven for during the beta?"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            {...register('intended_use', {
              required: 'A short description is required',
              maxLength: {
                value: 2000,
                message: 'Keep this under 2000 characters'
              }
            })}
          />
          {errors.intended_use && (
            <p className="text-sm text-destructive">
              {errors.intended_use.message}
            </p>
          )}
        </div>

        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            disabled={submitting}
            className="mt-1 h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-ring"
            {...register('roe_acknowledged', {
              required:
                'You must acknowledge the rules of engagement before submitting'
            })}
          />
          <span className="leading-snug text-foreground">
            I have read and agree to the{' '}
            <span className="font-medium">rules of engagement</span> and
            authorized-use policy. I understand all activity is logged and
            audited.
          </span>
        </label>
        {errors.roe_acknowledged && (
          <p className="-mt-3 text-sm text-destructive">
            {errors.roe_acknowledged.message}
          </p>
        )}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Registering…' : 'Register'}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have access?{' '}
        <Link
          to="/login"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
